import { Locale } from "../Locale.ts";

/**
 * UI strings for the MathFactsGenerator settings panel, the worksheet
 * preview chrome around it (PdfActions/PdfCanvasPreview), and the recent-
 * worksheets list beneath it (ADR 0051 part 2).
 *
 * `en` is the source of truth for which keys exist - every string here is
 * byte-identical to what the component rendered before extraction, per plan
 * §2.3. `es` and `fr` are checked against it with `satisfies`, so a locale
 * missing a key is a build error rather than a silent fallback to English
 * (plan §2.1; falsified once by deleting a key and watching `npm run build`
 * fail - see the PR/commit description).
 *
 * Enum values and their labels (`Labels.ts`) are untouched here - that is
 * part 3's business (ADR 0051 §Part-1-as-built).
 */
const en = {
    "settings.regrouping": "Regrouping",
    "settings.questionType": "Question type",
    "settings.largestNumberLabel": "Largest Number: ",
    "settings.largestNumberAria": "Largest number",
    "settings.rationalKindLabel": "Numbers: ",
    "settings.rationalKindAria": "Kind of number",
    "settings.listLengthLabel": "List Length: ",
    "settings.listLengthAria": "List length",
    "settings.chartScaleLabel": "Chart Scale: ",
    "settings.chartScaleAria": "Chart scale",
    "settings.questionsPerChartLabel": "Questions per Chart: ",
    "settings.questionsPerChartAria": "Questions per chart",
    "settings.linePlotScaleLabel": "Line Marked In: ",
    "settings.linePlotScaleAria": "Line marked in",
    "settings.linePlotMarkLabel": "Mark: ",
    "settings.linePlotMarkAria": "Mark",
    "settings.coordinateGridLabel": "Grid: ",
    "settings.coordinateGridAria": "Grid",
    "settings.calendarSpanLabel": "Count: ",
    "settings.calendarSpanAria": "How far to count",
    "settings.calendarMonthLabel": "Month: ",
    "settings.calendarMonthAria": "Month",
    "settings.printClassSet": "Print a set for a whole class",
    "settings.classRosterHint":
        "One name per line. Each student gets their own copy with their name in " +
        "the header. Nothing you type here is saved or shared — it is gone when " +
        "you close the tab.",
    "settings.classRosterAria": "Class roster, one name per line",
    "settings.classRosterPlaceholder": "Ava\nBen\nChloe",
    "settings.name": "name",
    "settings.names": "names",
    "settings.rosterOverflow": " · only the first {max} are used",
    "settings.fluencyChartTitle": "Tracking chart",
    "settings.fluencyChartHint": "A blank chart for these levels, with your roster's names down the side. Print it and write a date in the box when a student passes a level.",
    "settings.makeFluencyChart": "Make the tracking chart",
    "settings.buildingFluencyChart": "Building the chart…",
    "settings.openFluencyChart": "Open the tracking chart",
    "settings.classRosterLevelHint": "Add a level after a comma or tab to give a student their own: Maya, 6",
    "settings.rosterLevelOutOfRange": " · {count} with a level past the last one: they get level {level}",
    "settings.differentProblemsPerStudent": "Different problems per student",
    "settings.unitSystemAria": "Unit system",
    "settings.decimalPlacesLabel": "Decimal places: ",
    "settings.decimalPlacesAria": "Decimal places",
    "settings.largestWholeNumberLabel": "Largest whole number: ",
    "settings.largestWholeNumberAria": "Largest whole number",
    "settings.questionLabel": "Question: ",
    "settings.factFamilyQuestionAria": "Fact family question",
    "settings.fractionQuestionTypeAria": "Fraction question type",
    "settings.denominatorsAria": "Denominators",
    "settings.largestDenominatorLabel": "Largest Denominator: ",
    "settings.largestDenominatorAria": "Largest denominator",
    "settings.fractionModelNote":
        "Bounded lower than the arithmetic pages: a bar cut into more parts " +
        "than this gets hard to count at a glance, which makes the picture " +
        "ambiguous even though the fraction is exact.",
    "settings.fractionToDecimalNote":
        "Only fractions that convert to an exact decimal are used (halves, " +
        "quarters, fifths, eighths and tenths), so a larger limit adds fewer " +
        "options than it looks.",
    "settings.angleStepLabel": "Angles are multiples of: ",
    "settings.angleStepAria": "Angle step",
    "settings.printedLanguageLabel": "Worksheet language: ",
    "settings.printedLanguageAria": "Worksheet language",
    "settings.remaindersLabel": "Remainders",
    "settings.missingNumberLabel": "Missing number",
    "settings.missingNumberAria": "Missing number",
    "settings.moneyQuestionTypeAria": "Money question type",
    "settings.amountsLabel": "Amounts: ",
    "settings.moneyAmountsAria": "Money amounts",
    "settings.coinsLabel": "Coins: ",
    "settings.coinsAria": "Coins",
    "settings.numberLineTypeAria": "Number line type",
    "settings.problemsPerPageAria": "Number of problems per page",
    "settings.operationsToIncludeAria": "Operations to include",
    "settings.expressionsLabel": "Expressions: ",
    "settings.expressionLevelAria": "Expression level",
    "settings.halfPageLabel": "Half page (two per sheet)",
    "settings.toggleYes": "Yes",
    "settings.toggleNo": "No",
    "settings.timedTestLabel": "Timed test — time & score box",
    "settings.negativeNumbersLabel": "Negative numbers",
    "settings.addPageAria": "Add a page",
    "settings.removePageAria": "Remove a page",
    "settings.showWorkingLabel": "Show the working",
    "settings.differentiationSetLabel": "Differentiation set (3 levels)",
    "settings.storyTypesLabel": "Story types",
    "settings.storyTypesAria": "Story types",
    "settings.storyTypesHint": "Varied adds stories where the operation is not the obvious one, and mixes the operations evenly on every page.",
    "settings.factLevelsLabel": "Fact levels — a test per level",
    "settings.factLevel": "Level",
    "settings.factLevelOption": "{n} · {name} · {count} facts",
    "settings.factLevelCeiling": "Tables up to",
    "settings.factLevelTo10": "10",
    "settings.factLevelTo12": "12",
    "settings.factLevelSheet": "Sheet",
    "settings.factLevelSheetTest": "Test",
    "settings.factLevelSheetPractice": "Home practice",
    "settings.factLevelCrossed": "Levels work within addition and subtraction, or within multiplication and division. This sheet uses its number ranges instead.",
    "settings.patternTypeAria": "Pattern type",
    "settings.termsShownLabel": "Terms Shown: ",
    "settings.termsShownAria": "Terms shown",
    "settings.numberOfDigitsLabel": "Number of Digits: ",
    "settings.placeValueDigitsAria": "Place value digits",
    "settings.layoutHelpLabel": "Layout help",
    "settings.carryBoxesLabel": "Carry boxes",
    "settings.alignmentGridLabel": "Alignment grid",
    "settings.largePrintLabel": "Large print",
    "settings.selectPrefix": "Select ",
    "settings.selectAllNumbers": "Select all numbers",
    "settings.clearAllNumbers": "Clear all numbers",
    "settings.forValueRangeSuffix": " for value range {id}",
    "settings.all": "All",
    "settings.clear": "Clear",
    "settings.showAsLabel": "Show as: ",
    "settings.roundingDisplayAria": "Rounding display",
    "settings.numbersLabel": "Numbers: ",
    "settings.whole": "Whole",
    "settings.decimalPlacesAbbrev": "dp",
    "settings.numberOfDigitsAria": "Number of digits",
    "settings.roundingPlaceAria": "Rounding place",
    "settings.longestSideLabel": "Longest Side: ",
    "settings.longestSideAria": "Longest side",
    "settings.longestEdgeLabel": "Longest Edge: ",
    "settings.longestEdgeAria": "Longest edge",
    "settings.largestRadiusOrHeightLabel": "Largest Radius or Height: ",
    "settings.largestRadiusOrHeightAria": "Largest radius or height",
    "settings.largestRadiusLabel": "Largest Radius: ",
    "settings.largestRadiusAria": "Largest radius",
    "settings.copyLinkButton": "Copy link to this worksheet",
    "settings.shareHint":
        "Opens this exact worksheet — the same problems, in the same order — " +
        "for anyone you send it to.",
    "settings.linkCopied": "Link copied",
    "settings.shareManualMessage": "Your browser would not let us copy it. Here is the link:",
    "settings.linkToWorksheetAria": "Link to this worksheet",
    "settings.sectionsUpTo": "Sections (up to {max}):",
    "settings.more": "More ",
    "settings.fewer": "Fewer ",
    "settings.showTimesAsLabel": "Show the times as: ",
    "settings.timeDisplayAria": "Time display",
    "settings.timesToTheLabel": "Times To The: ",
    "settings.timeIntervalAria": "Time interval",
    "settings.crossNoonLabel": "Cross noon (a.m. to p.m.)",
    "settings.numberOfDigitsForRangeAria": "Number of digits for value range {id}",
    "settings.numbersToUseInRangeAria": "Numbers to use in value range {id}",
    "settings.hideAdvancedOptions": "Hide advanced options",
    "settings.advancedOptionsOn": "Advanced options ({n} on)",
    "settings.advancedOptions": "Advanced options",
    "settings.recentWorksheetsHeading": "Worksheets you made recently",
    "settings.recentWorksheetsHint":
        "Kept in this browser only. Opening one brings back that exact worksheet — " +
        "the same problems, in the same order.",
    "settings.removeWorksheet": "Remove {label}",
    "settings.clearThisList": "Clear this list",
    "settings.frequentlyAskedQuestions": "Frequently asked questions",
    "settings.packContents": "In this pack",
    "settings.packCovers": "What this pack covers",
    "settings.checkCovers": "What this check covers",
    "settings.checkContents": "What the check samples",
    "settings.packIdPrefix": "Pack ID",
    "settings.checkIdPrefix": "Check ID",
    "settings.catchUpTitle": "Make a catch-up pack",
    "settings.catchUpHint": "Tick the skills a child missed and make a pack of those pages, one sheet for each, with the answers behind. Nothing you tick is saved or shared: it is gone when you close the tab.",
    "settings.catchUpSkills": "Skills to practise",
    "settings.catchUpFact": "Fact practice, starting at level",
    "settings.catchUpNoFact": "No fact practice",
    "settings.catchUpLevelOption": "{n} · {name}",
    "settings.catchUpCount": "{n} of {max} chosen",
    "settings.catchUpMake": "Make the catch-up pack",
    "settings.loadingPreview": "Loading preview…",
    "settings.cbnShowFinished": "Show the finished picture",
    "settings.cbnHideFinished": "Hide the finished picture",
    "settings.cbnFinishedCaption": "This is how every color-by-number sheet looks once each square is colored by its answer. The problems change with every print; the picture does not.",
    "settings.cbnFinishedAria": "The finished picture: a black and white border collie with brown eyes and a pink tongue, on blue sky and green grass",
    "settings.cbnAddAnswerPages": "Print it as an answer page too",
    "settings.cbnAnswerPagesOn": "Answer pages are on: each worksheet is followed by the finished picture, with every answer written in its square.",
    "settings.tierShortfall":
        "These settings are already at the {level} level — the set will have " +
        "{n} sheets, not three.",
    "settings.easiest": "easiest",
    "settings.hardest": "hardest",
    "settings.reportBugEmail":
        "To report a bug or request a feature, please email mathpageshelp@gmail.com",
    "settings.newProblems": "New problems",
    "settings.rebuildHint": "Type the code from the bottom of a printed sheet",
    "settings.rebuildButton": "Rebuild",
    "settings.previewOfWorksheetPage1": "Preview of worksheet page 1",
    "settings.couldNotBuildPdf": "Could not build the PDF — change a setting and try again",
    "settings.pdfOpenHintPrefix": "Opens in a new tab, where you can print it — or ",
    "settings.savePdfLink": "save the PDF",
    "settings.pdfOpenHintSuffix": " instead.",
    "settings.updatingPdf": "Updating…",
} as const;

export type SettingsStringKey = keyof typeof en;

const es = {
    "settings.regrouping": "Reagrupación",
    "settings.questionType": "Tipo de pregunta",
    "settings.largestNumberLabel": "Número más grande: ",
    "settings.largestNumberAria": "Número más grande",
    "settings.rationalKindLabel": "Números: ",
    "settings.rationalKindAria": "Tipo de número",
    "settings.listLengthLabel": "Longitud de la lista: ",
    "settings.listLengthAria": "Longitud de la lista",
    "settings.chartScaleLabel": "Escala del gráfico: ",
    "settings.chartScaleAria": "Escala del gráfico",
    "settings.questionsPerChartLabel": "Preguntas por gráfico: ",
    "settings.questionsPerChartAria": "Preguntas por gráfico",
    "settings.linePlotScaleLabel": "Recta marcada en: ",
    "settings.linePlotScaleAria": "Recta marcada en",
    "settings.linePlotMarkLabel": "Marca: ",
    "settings.linePlotMarkAria": "Marca",
    "settings.coordinateGridLabel": "Cuadrícula: ",
    "settings.coordinateGridAria": "Cuadrícula",
    "settings.calendarSpanLabel": "Contar: ",
    "settings.calendarSpanAria": "Cuánto contar",
    "settings.calendarMonthLabel": "Mes: ",
    "settings.calendarMonthAria": "Mes",
    "settings.printClassSet": "Imprimir un juego para toda la clase",
    "settings.classRosterHint":
        "Un nombre por línea. Cada estudiante recibe su propia copia con su " +
        "nombre en el encabezado. Nada de lo que escribas aquí se guarda ni se " +
        "comparte: desaparece al cerrar la pestaña.",
    "settings.classRosterAria": "Lista de la clase, un nombre por línea",
    "settings.classRosterPlaceholder": "Ana\nBenito\nClara",
    "settings.name": "nombre",
    "settings.names": "nombres",
    "settings.rosterOverflow": " · solo se usan los primeros {max}",
    "settings.fluencyChartTitle": "Tabla de seguimiento",
    "settings.fluencyChartHint": "Una tabla en blanco para estos niveles, con los nombres de tu lista al lado. Imprímela y anota una fecha en la casilla cuando un estudiante supere un nivel.",
    "settings.makeFluencyChart": "Crear la tabla de seguimiento",
    "settings.buildingFluencyChart": "Creando la tabla…",
    "settings.openFluencyChart": "Abrir la tabla de seguimiento",
    "settings.classRosterLevelHint": "Añade un nivel tras una coma o tabulación para dar a un estudiante el suyo: Maya, 6",
    "settings.rosterLevelOutOfRange": " · {count} con un nivel más allá del último: usan el nivel {level}",
    "settings.differentProblemsPerStudent": "Problemas distintos por estudiante",
    "settings.unitSystemAria": "Sistema de unidades",
    "settings.decimalPlacesLabel": "Cifras decimales: ",
    "settings.decimalPlacesAria": "Cifras decimales",
    "settings.largestWholeNumberLabel": "Número entero más grande: ",
    "settings.largestWholeNumberAria": "Número entero más grande",
    "settings.questionLabel": "Pregunta: ",
    "settings.factFamilyQuestionAria": "Pregunta de familia de operaciones",
    "settings.fractionQuestionTypeAria": "Tipo de pregunta de fracciones",
    "settings.denominatorsAria": "Denominadores",
    "settings.largestDenominatorLabel": "Denominador más grande: ",
    "settings.largestDenominatorAria": "Denominador más grande",
    "settings.fractionModelNote":
        "El límite es menor que en las páginas de cálculo: una barra dividida " +
        "en más partes que esto es difícil de contar de un vistazo, lo que " +
        "hace ambigua la imagen aunque la fracción sea exacta.",
    "settings.fractionToDecimalNote":
        "Solo se usan fracciones que se convierten en un decimal exacto " +
        "(medios, cuartos, quintos, octavos y décimos), así que un límite " +
        "mayor añade menos opciones de lo que parece.",
    "settings.angleStepLabel": "Los ángulos son múltiplos de: ",
    "settings.angleStepAria": "Incremento de ángulo",
    "settings.printedLanguageLabel": "Idioma de la hoja: ",
    "settings.printedLanguageAria": "Idioma de la hoja",
    "settings.remaindersLabel": "Restos",
    "settings.missingNumberLabel": "Número que falta",
    "settings.missingNumberAria": "Número que falta",
    "settings.moneyQuestionTypeAria": "Tipo de pregunta de dinero",
    "settings.amountsLabel": "Cantidades: ",
    "settings.moneyAmountsAria": "Cantidades de dinero",
    "settings.coinsLabel": "Monedas: ",
    "settings.coinsAria": "Monedas",
    "settings.numberLineTypeAria": "Tipo de recta numérica",
    "settings.problemsPerPageAria": "Número de problemas por página",
    "settings.operationsToIncludeAria": "Operaciones a incluir",
    "settings.expressionsLabel": "Expresiones: ",
    "settings.expressionLevelAria": "Nivel de la expresión",
    "settings.halfPageLabel": "Media página",
    "settings.toggleYes": "Sí",
    "settings.toggleNo": "No",
    "settings.timedTestLabel": "Prueba cronometrada",
    "settings.negativeNumbersLabel": "Números negativos",
    "settings.addPageAria": "Añadir una página",
    "settings.removePageAria": "Quitar una página",
    "settings.showWorkingLabel": "Mostrar el procedimiento",
    "settings.differentiationSetLabel": "Conjunto diferenciado (3 niveles)",
    "settings.storyTypesLabel": "Tipos de problema",
    "settings.storyTypesAria": "Tipos de problema",
    "settings.storyTypesHint": "Variadas añade problemas en los que la operación no es la evidente, y reparte las operaciones por igual en cada página.",
    "settings.factLevelsLabel": "Niveles de operaciones — una prueba por nivel",
    "settings.factLevel": "Nivel",
    "settings.factLevelOption": "{n} · {name} · {count} operaciones",
    "settings.factLevelCeiling": "Tablas hasta",
    "settings.factLevelTo10": "10",
    "settings.factLevelTo12": "12",
    "settings.factLevelSheet": "Hoja",
    "settings.factLevelSheetTest": "Prueba",
    "settings.factLevelSheetPractice": "Práctica en casa",
    "settings.factLevelCrossed": "Los niveles funcionan dentro de la suma y la resta, o dentro de la multiplicación y la división. Esta hoja usa sus rangos de números.",
    "settings.patternTypeAria": "Tipo de patrón",
    "settings.termsShownLabel": "Términos mostrados: ",
    "settings.termsShownAria": "Términos mostrados",
    "settings.numberOfDigitsLabel": "Número de dígitos: ",
    "settings.placeValueDigitsAria": "Dígitos de valor posicional",
    "settings.layoutHelpLabel": "Ayudas de presentación",
    "settings.carryBoxesLabel": "Casillas de llevada",
    "settings.alignmentGridLabel": "Cuadrícula de alineación",
    "settings.largePrintLabel": "Letra grande",
    "settings.selectPrefix": "Seleccionar ",
    "settings.selectAllNumbers": "Seleccionar todos los números",
    "settings.clearAllNumbers": "Borrar todos los números",
    "settings.forValueRangeSuffix": " para el rango de valores {id}",
    "settings.all": "Todos",
    "settings.clear": "Borrar",
    "settings.showAsLabel": "Mostrar como: ",
    "settings.roundingDisplayAria": "Formato de redondeo",
    "settings.numbersLabel": "Números: ",
    "settings.whole": "Entero",
    "settings.decimalPlacesAbbrev": "dec",
    "settings.numberOfDigitsAria": "Número de dígitos",
    "settings.roundingPlaceAria": "Posición de redondeo",
    "settings.longestSideLabel": "Lado más largo: ",
    "settings.longestSideAria": "Lado más largo",
    "settings.longestEdgeLabel": "Arista más larga: ",
    "settings.longestEdgeAria": "Arista más larga",
    "settings.largestRadiusOrHeightLabel": "Radio o altura máxima: ",
    "settings.largestRadiusOrHeightAria": "Radio o altura máxima",
    "settings.largestRadiusLabel": "Radio máximo: ",
    "settings.largestRadiusAria": "Radio máximo",
    "settings.copyLinkButton": "Copiar enlace a esta hoja de ejercicios",
    "settings.shareHint":
        "Abre esta misma hoja de ejercicios —los mismos problemas, en el mismo " +
        "orden— para quien se la envíes.",
    "settings.linkCopied": "Enlace copiado",
    "settings.shareManualMessage": "Tu navegador no nos dejó copiarlo. Aquí tienes el enlace:",
    "settings.linkToWorksheetAria": "Enlace a esta hoja de ejercicios",
    "settings.sectionsUpTo": "Secciones (hasta {max}):",
    "settings.more": "Más ",
    "settings.fewer": "Menos ",
    "settings.showTimesAsLabel": "Mostrar las horas como: ",
    "settings.timeDisplayAria": "Formato de la hora",
    "settings.timesToTheLabel": "Precisión: ",
    "settings.timeIntervalAria": "Intervalo de tiempo",
    "settings.crossNoonLabel": "Pasar el mediodía (a. m. a p. m.)",
    "settings.numberOfDigitsForRangeAria": "Número de dígitos para el rango de valores {id}",
    "settings.numbersToUseInRangeAria": "Números que se pueden usar en el rango de valores {id}",
    "settings.hideAdvancedOptions": "Ocultar opciones avanzadas",
    "settings.advancedOptionsOn": "Opciones avanzadas ({n} activas)",
    "settings.advancedOptions": "Opciones avanzadas",
    "settings.recentWorksheetsHeading": "Hojas de ejercicios que hiciste recientemente",
    "settings.recentWorksheetsHint":
        "Se guarda solo en este navegador. Al abrir una se recupera esa misma " +
        "hoja de ejercicios —los mismos problemas, en el mismo orden.",
    "settings.removeWorksheet": "Quitar {label}",
    "settings.clearThisList": "Borrar esta lista",
    "settings.frequentlyAskedQuestions": "Preguntas frecuentes",
    "settings.packContents": "En este paquete",
    "settings.packCovers": "Qué cubre este paquete",
    "settings.checkCovers": "Qué cubre esta prueba",
    "settings.checkContents": "Qué evalúa la prueba",
    "settings.packIdPrefix": "ID del paquete",
    "settings.checkIdPrefix": "ID de la prueba",
    "settings.catchUpTitle": "Crear un paquete de repaso",
    "settings.catchUpHint": "Marca las destrezas que un niño no logró y crea un paquete con esas páginas, una hoja para cada una, con las respuestas detrás. Nada de lo que marques se guarda ni se comparte: desaparece al cerrar la pestaña.",
    "settings.catchUpSkills": "Destrezas para practicar",
    "settings.catchUpFact": "Práctica de operaciones, desde el nivel",
    "settings.catchUpNoFact": "Sin práctica de operaciones",
    "settings.catchUpLevelOption": "{n} · {name}",
    "settings.catchUpCount": "{n} de {max} elegidas",
    "settings.catchUpMake": "Crear el paquete de repaso",
    "settings.loadingPreview": "Cargando vista previa…",
    "settings.cbnShowFinished": "Mostrar el dibujo terminado",
    "settings.cbnHideFinished": "Ocultar el dibujo terminado",
    "settings.cbnFinishedCaption": "Así queda cada hoja de colorear por número cuando cada casilla se colorea según su resultado. Los problemas cambian en cada impresión; el dibujo no.",
    "settings.cbnFinishedAria": "El dibujo terminado: una border collie blanca y negra, de ojos marrones y lengua rosa, sobre cielo azul y hierba verde",
    "settings.cbnAddAnswerPages": "Imprimirlo también como hoja de respuestas",
    "settings.cbnAnswerPagesOn": "Las hojas de respuestas están activadas: después de cada hoja se imprime el dibujo terminado, con cada resultado en su casilla.",
    "settings.tierShortfall":
        "Estos ajustes ya están en el nivel {level}: el conjunto tendrá {n} " +
        "hojas, no tres.",
    "settings.easiest": "más fácil",
    "settings.hardest": "más difícil",
    "settings.reportBugEmail":
        "Para informar de un error o solicitar una función, escribe a mathpageshelp@gmail.com",
    "settings.newProblems": "Nuevos problemas",
    "settings.rebuildHint": "Escribe el código que aparece al pie de una hoja impresa",
    "settings.rebuildButton": "Reconstruir",
    "settings.previewOfWorksheetPage1": "Vista previa de la página 1 de la hoja de ejercicios",
    "settings.couldNotBuildPdf": "No se pudo generar el PDF. Cambia un ajuste e inténtalo de nuevo.",
    "settings.pdfOpenHintPrefix": "Se abre en una pestaña nueva, donde puedes imprimirla, o ",
    "settings.savePdfLink": "guardar el PDF",
    "settings.pdfOpenHintSuffix": " en su lugar.",
    "settings.updatingPdf": "Actualizando…",
} satisfies Record<SettingsStringKey, string>;

const fr = {
    "settings.regrouping": "Retenue",
    "settings.questionType": "Type de question",
    "settings.largestNumberLabel": "Nombre le plus grand : ",
    "settings.largestNumberAria": "Nombre le plus grand",
    "settings.rationalKindLabel": "Nombres : ",
    "settings.rationalKindAria": "Type de nombre",
    "settings.listLengthLabel": "Longueur de la liste : ",
    "settings.listLengthAria": "Longueur de la liste",
    "settings.chartScaleLabel": "Échelle du graphique : ",
    "settings.chartScaleAria": "Échelle du graphique",
    "settings.questionsPerChartLabel": "Questions par graphique : ",
    "settings.questionsPerChartAria": "Questions par graphique",
    "settings.linePlotScaleLabel": "Droite graduée en : ",
    "settings.linePlotScaleAria": "Droite graduée en",
    "settings.linePlotMarkLabel": "Marque : ",
    "settings.linePlotMarkAria": "Marque",
    "settings.coordinateGridLabel": "Quadrillage : ",
    "settings.coordinateGridAria": "Quadrillage",
    "settings.calendarSpanLabel": "Compter : ",
    "settings.calendarSpanAria": "Jusqu'où compter",
    "settings.calendarMonthLabel": "Mois : ",
    "settings.calendarMonthAria": "Mois",
    "settings.printClassSet": "Imprimer un jeu pour toute la classe",
    "settings.classRosterHint":
        "Un nom par ligne. Chaque élève reçoit son propre exemplaire avec son " +
        "nom dans l'en-tête. Rien de ce que vous saisissez ici n'est enregistré " +
        "ni partagé : tout disparaît à la fermeture de l'onglet.",
    "settings.classRosterAria": "Liste de la classe, un nom par ligne",
    "settings.classRosterPlaceholder": "Ana\nBenoît\nChloé",
    "settings.name": "nom",
    "settings.names": "noms",
    "settings.rosterOverflow": " · seuls les {max} premiers sont utilisés",
    "settings.fluencyChartTitle": "Tableau de suivi",
    "settings.fluencyChartHint": "Un tableau vierge pour ces niveaux, avec les noms de votre liste sur le côté. Imprimez-le et notez une date dans la case quand un élève réussit un niveau.",
    "settings.makeFluencyChart": "Créer le tableau de suivi",
    "settings.buildingFluencyChart": "Création du tableau…",
    "settings.openFluencyChart": "Ouvrir le tableau de suivi",
    "settings.classRosterLevelHint": "Ajoutez un niveau après une virgule ou une tabulation pour donner à un élève le sien : Maya, 6",
    "settings.rosterLevelOutOfRange": " · {count} avec un niveau au-delà du dernier : ils prennent le niveau {level}",
    "settings.differentProblemsPerStudent": "Problèmes différents par élève",
    "settings.unitSystemAria": "Système d'unités",
    "settings.decimalPlacesLabel": "Décimales : ",
    "settings.decimalPlacesAria": "Décimales",
    "settings.largestWholeNumberLabel": "Plus grand nombre entier : ",
    "settings.largestWholeNumberAria": "Plus grand nombre entier",
    "settings.questionLabel": "Question : ",
    "settings.factFamilyQuestionAria": "Question de famille d'opérations",
    "settings.fractionQuestionTypeAria": "Type de question sur les fractions",
    "settings.denominatorsAria": "Dénominateurs",
    "settings.largestDenominatorLabel": "Plus grand dénominateur : ",
    "settings.largestDenominatorAria": "Plus grand dénominateur",
    "settings.fractionModelNote":
        "La limite est plus basse que sur les pages de calcul : une barre " +
        "découpée en plus de parts que cela devient difficile à compter d'un " +
        "coup d'œil, ce qui rend l'image ambiguë même quand la fraction est " +
        "exacte.",
    "settings.fractionToDecimalNote":
        "Seules les fractions qui donnent une décimale exacte sont utilisées " +
        "(demis, quarts, cinquièmes, huitièmes et dixièmes), donc une limite " +
        "plus élevée ajoute moins d'options qu'il n'y paraît.",
    "settings.angleStepLabel": "Les angles sont des multiples de : ",
    // "Pas de l'angle" was ambiguous read aloud: French "pas" is both "step"
    // and the negator, so a screen reader announced something a listener could
    // take as "no angle". The visible label carries the context a sighted
    // reader has; an aria-label has to stand alone. Matches Spanish's choice.
    "settings.angleStepAria": "Incrément de l'angle",
    "settings.printedLanguageLabel": "Langue de la fiche : ",
    "settings.printedLanguageAria": "Langue de la fiche",
    "settings.remaindersLabel": "Restes",
    "settings.missingNumberLabel": "Nombre manquant",
    "settings.missingNumberAria": "Nombre manquant",
    "settings.moneyQuestionTypeAria": "Type de question sur l'argent",
    "settings.amountsLabel": "Montants : ",
    "settings.moneyAmountsAria": "Montants en argent",
    "settings.coinsLabel": "Pièces : ",
    "settings.coinsAria": "Pièces",
    "settings.numberLineTypeAria": "Type de droite numérique",
    "settings.problemsPerPageAria": "Nombre de problèmes par page",
    "settings.operationsToIncludeAria": "Opérations à inclure",
    "settings.expressionsLabel": "Expressions : ",
    "settings.expressionLevelAria": "Niveau de l'expression",
    "settings.halfPageLabel": "Demi-page",
    "settings.toggleYes": "Oui",
    "settings.toggleNo": "Non",
    "settings.timedTestLabel": "Test chronométré",
    "settings.negativeNumbersLabel": "Nombres négatifs",
    "settings.addPageAria": "Ajouter une page",
    "settings.removePageAria": "Supprimer une page",
    "settings.showWorkingLabel": "Afficher les étapes du calcul",
    "settings.differentiationSetLabel": "Ensemble différencié (3 niveaux)",
    "settings.storyTypesLabel": "Types de problèmes",
    "settings.storyTypesAria": "Types de problèmes",
    "settings.storyTypesHint": "Variées ajoute des problèmes où l’opération n’est pas évidente, et répartit les opérations à égalité sur chaque page.",
    "settings.factLevelsLabel": "Niveaux de faits — un test par niveau",
    "settings.factLevel": "Niveau",
    "settings.factLevelOption": "{n} · {name} · {count} faits",
    "settings.factLevelCeiling": "Tables jusqu’à",
    "settings.factLevelTo10": "10",
    "settings.factLevelTo12": "12",
    "settings.factLevelSheet": "Feuille",
    "settings.factLevelSheetTest": "Test",
    "settings.factLevelSheetPractice": "Entraînement à la maison",
    "settings.factLevelCrossed": "Les niveaux fonctionnent dans l’addition et la soustraction, ou dans la multiplication et la division. Cette feuille utilise ses plages de nombres.",
    "settings.patternTypeAria": "Type de suite",
    "settings.termsShownLabel": "Termes affichés : ",
    "settings.termsShownAria": "Termes affichés",
    "settings.numberOfDigitsLabel": "Nombre de chiffres : ",
    "settings.placeValueDigitsAria": "Chiffres de la valeur de position",
    "settings.layoutHelpLabel": "Aides de mise en page",
    "settings.carryBoxesLabel": "Cases de retenue",
    "settings.alignmentGridLabel": "Grille d'alignement",
    "settings.largePrintLabel": "Grands caractères",
    "settings.selectPrefix": "Sélectionner ",
    "settings.selectAllNumbers": "Sélectionner tous les nombres",
    "settings.clearAllNumbers": "Effacer tous les nombres",
    "settings.forValueRangeSuffix": " pour la plage de valeurs {id}",
    "settings.all": "Tous",
    "settings.clear": "Effacer",
    "settings.showAsLabel": "Afficher comme : ",
    "settings.roundingDisplayAria": "Affichage de l'arrondi",
    "settings.numbersLabel": "Nombres : ",
    "settings.whole": "Entier",
    "settings.decimalPlacesAbbrev": "déc",
    "settings.numberOfDigitsAria": "Nombre de chiffres",
    "settings.roundingPlaceAria": "Position d'arrondi",
    "settings.longestSideLabel": "Côté le plus long : ",
    "settings.longestSideAria": "Côté le plus long",
    "settings.longestEdgeLabel": "Arête la plus longue : ",
    "settings.longestEdgeAria": "Arête la plus longue",
    "settings.largestRadiusOrHeightLabel": "Rayon ou hauteur maximal : ",
    "settings.largestRadiusOrHeightAria": "Rayon ou hauteur maximal",
    "settings.largestRadiusLabel": "Rayon maximal : ",
    "settings.largestRadiusAria": "Rayon maximal",
    "settings.copyLinkButton": "Copier le lien de cette fiche d'exercices",
    "settings.shareHint":
        "Ouvre exactement cette fiche d'exercices — les mêmes problèmes, dans " +
        "le même ordre — pour toute personne à qui vous l'envoyez.",
    "settings.linkCopied": "Lien copié",
    "settings.shareManualMessage": "Votre navigateur n'a pas permis de le copier. Voici le lien :",
    "settings.linkToWorksheetAria": "Lien vers cette fiche d'exercices",
    "settings.sectionsUpTo": "Sections (jusqu'à {max}) :",
    "settings.more": "Plus de ",
    "settings.fewer": "Moins de ",
    "settings.showTimesAsLabel": "Afficher les heures comme : ",
    "settings.timeDisplayAria": "Affichage de l'heure",
    "settings.timesToTheLabel": "Précision : ",
    "settings.timeIntervalAria": "Intervalle de temps",
    "settings.crossNoonLabel": "Passer midi (avant midi à après midi)",
    "settings.numberOfDigitsForRangeAria": "Nombre de chiffres pour la plage de valeurs {id}",
    "settings.numbersToUseInRangeAria": "Nombres utilisables dans la plage de valeurs {id}",
    "settings.hideAdvancedOptions": "Masquer les options avancées",
    "settings.advancedOptionsOn": "Options avancées ({n} activées)",
    "settings.advancedOptions": "Options avancées",
    "settings.recentWorksheetsHeading": "Fiches que vous avez créées récemment",
    "settings.recentWorksheetsHint":
        "Conservé uniquement dans ce navigateur. En ouvrir une restaure " +
        "exactement cette fiche d'exercices — les mêmes problèmes, dans le même ordre.",
    "settings.removeWorksheet": "Supprimer {label}",
    "settings.clearThisList": "Effacer cette liste",
    "settings.frequentlyAskedQuestions": "Questions fréquentes",
    "settings.packContents": "Dans ce paquet",
    "settings.packCovers": "Ce que couvre ce paquet",
    "settings.checkCovers": "Ce que couvre ce test",
    "settings.checkContents": "Ce que le test évalue",
    "settings.packIdPrefix": "ID du paquet",
    "settings.checkIdPrefix": "ID du test",
    "settings.catchUpTitle": "Créer un paquet de rattrapage",
    "settings.catchUpHint": "Cochez les compétences qu’un enfant n’a pas réussies et créez un paquet de ces pages, une feuille pour chacune, avec les réponses derrière. Rien de ce que vous cochez n’est enregistré ni partagé : tout disparaît à la fermeture de l’onglet.",
    "settings.catchUpSkills": "Compétences à travailler",
    "settings.catchUpFact": "Entraînement aux faits, à partir du niveau",
    "settings.catchUpNoFact": "Pas d’entraînement aux faits",
    "settings.catchUpLevelOption": "{n} · {name}",
    "settings.catchUpCount": "{n} choisies sur {max}",
    "settings.catchUpMake": "Créer le paquet de rattrapage",
    "settings.loadingPreview": "Chargement de l'aperçu…",
    "settings.cbnShowFinished": "Afficher le dessin terminé",
    "settings.cbnHideFinished": "Masquer le dessin terminé",
    "settings.cbnFinishedCaption": "Voici chaque fiche de coloriage magique une fois chaque case coloriée selon son résultat. Les calculs changent à chaque impression ; le dessin, non.",
    "settings.cbnFinishedAria": "Le dessin terminé : une border collie noire et blanche aux yeux marron et à la langue rose, sur un ciel bleu et de l'herbe verte",
    "settings.cbnAddAnswerPages": "L'imprimer aussi comme page de réponses",
    "settings.cbnAnswerPagesOn": "Les pages de réponses sont activées : chaque fiche est suivie du dessin terminé, avec chaque résultat dans sa case.",
    "settings.tierShortfall":
        "Ces réglages sont déjà au niveau {level} : le jeu comportera {n} " +
        "fiches, pas trois.",
    "settings.easiest": "le plus facile",
    "settings.hardest": "le plus difficile",
    "settings.reportBugEmail":
        "Pour signaler un problème ou demander une fonctionnalité, écrivez à mathpageshelp@gmail.com",
    "settings.newProblems": "Nouveaux problèmes",
    "settings.rebuildHint": "Saisissez le code au bas d'une fiche imprimée",
    "settings.rebuildButton": "Reconstruire",
    "settings.previewOfWorksheetPage1": "Aperçu de la page 1 de la fiche d'exercices",
    "settings.couldNotBuildPdf": "Impossible de générer le PDF. Modifiez un réglage et réessayez.",
    "settings.pdfOpenHintPrefix": "S'ouvre dans un nouvel onglet, où vous pouvez l'imprimer, ou ",
    "settings.savePdfLink": "enregistrer le PDF",
    "settings.pdfOpenHintSuffix": " à la place.",
    "settings.updatingPdf": "Mise à jour…",
} satisfies Record<SettingsStringKey, string>;

export const settingsStrings: Record<Locale, Record<SettingsStringKey, string>> = { en, es, fr };
